import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Badge, gbp, Loading } from "@/components/ui-bits";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Plus, BedDouble, Bath, MapPin, X, Loader2, FileDown, ShieldCheck, AlertTriangle, ShieldAlert, KeyRound, Eye, EyeOff, Trash2, RotateCcw, Archive } from "lucide-react";
import { toast } from "sonner";
import { fileUrl } from "@/lib/api";

const AccessCodes = ({ property }) => {
  const [editing, setEditing] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [ks, setKs] = useState(property.key_safe_code || "");
  const [kc, setKc] = useState(property.key_code || "");
  const [savedKs, setSavedKs] = useState(property.key_safe_code || "");
  const [savedKc, setSavedKc] = useState(property.key_code || "");
  const [saving, setSaving] = useState(false);
  const stop = (e) => e.stopPropagation();

  const save = async (e) => {
    stop(e);
    setSaving(true);
    try {
      await api.patch(`/properties/${property.property_id}/access-codes`, { key_safe_code: ks, key_code: kc });
      setSavedKs(ks); setSavedKc(kc); setEditing(false);
      toast.success("Access codes saved");
    } catch { toast.error("Could not save codes"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-2 pt-2 border-t border-slate-100" onClick={stop} data-testid={`access-codes-${property.reference}`}>
      {editing ? (
        <div className="space-y-1.5">
          <label className="block text-[11px] text-slate-500">Key safe code
            <input value={ks} onChange={(e) => setKs(e.target.value)} onClick={stop} data-testid={`key-safe-input-${property.reference}`} placeholder="e.g. 4821" className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </label>
          <label className="block text-[11px] text-slate-500">Key code
            <input value={kc} onChange={(e) => setKc(e.target.value)} onClick={stop} data-testid={`key-code-input-${property.reference}`} placeholder="e.g. C1234" className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </label>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} data-testid={`save-codes-${property.reference}`} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-md py-1 text-xs font-semibold flex items-center justify-center gap-1">{saving ? <Loader2 size={12} className="animate-spin" /> : null} Save</button>
            <button onClick={(e) => { stop(e); setEditing(false); setKs(savedKs); setKc(savedKc); }} className="px-2 rounded-md border border-slate-300 text-slate-500 text-xs">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <button onClick={(e) => { stop(e); setEditing(true); }} data-testid={`edit-codes-${property.reference}`} className="w-full flex items-center justify-between text-xs group">
            <span className="flex items-center gap-1.5 text-slate-500"><KeyRound size={13} className="text-amber-500" /> Key safe & key codes</span>
            <span className="text-slate-400 group-hover:text-blue-700 font-medium">{savedKs || savedKc ? "Edit" : "Add"}</span>
          </button>
          {(savedKs || savedKc) && (
            <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-600">
              <span>Safe: <b>{reveal ? (savedKs || "—") : "••••"}</b></span>
              <span>Key: <b>{reveal ? (savedKc || "—") : "••••"}</b></span>
              <button onClick={(e) => { stop(e); setReveal((r) => !r); }} data-testid={`reveal-codes-${property.reference}`} className="ml-auto flex items-center gap-1 text-blue-700 hover:underline">{reveal ? <EyeOff size={11} /> : <Eye size={11} />}{reveal ? "Hide" : "Show"}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ReadyToLetBadge = ({ c }) => {
  if (!c) return null;
  const map = {
    ready: { cls: "bg-emerald-600/95 text-white", Icon: ShieldCheck, label: "Ready to let" },
    expiring: { cls: "bg-amber-500/95 text-white", Icon: AlertTriangle, label: `Expiring in ${c.days}d` },
    not_ready: { cls: "bg-red-600/95 text-white", Icon: ShieldAlert, label: `Missing: ${(c.missing || []).join(", ")}` },
  };
  const { cls, Icon, label } = map[c.status] || map.not_ready;
  return (
    <span data-testid={`ready-to-let-${c.status}`} title={c.status === "not_ready" ? `Missing/expired: ${(c.missing || []).join(", ")}` : label}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold backdrop-blur ${cls}`}>
      <Icon size={11} /> {label}
    </span>
  );
};

export default function Properties() {
  const [properties, setProperties] = useState(null);
  const [view, setView] = useState("active");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ reference: "", address: "", postcode: "", city: "", beds: 2, baths: 1, rent_pcm: 0, landlord: "", tenant: "", status: "Managed" });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim().toLowerCase();

  const load = () => { setProperties(null); return api.get(`/properties${view === "archived" ? "?archived=true" : ""}`).then(({ data }) => setProperties(data)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view]);

  const archive = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm(`Archive property ${p.address}?\n\nIt will be moved to the archive and kept for 7 years (then automatically deleted). You can restore it any time before then from the Archived tab.`)) return;
    try {
      const { data } = await api.delete(`/properties/${p.property_id}`);
      toast.success(data.message || "Property archived");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not archive property"); }
  };

  const restore = async (e, p) => {
    e.stopPropagation();
    try {
      await api.post(`/properties/${p.property_id}/restore`);
      toast.success(`${p.address} restored`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not restore property"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/properties", { ...form, beds: +form.beds, baths: +form.baths, rent_pcm: +form.rent_pcm });
      toast.success("Property added to portfolio");
      setOpen(false);
      setForm({ reference: "", address: "", postcode: "", city: "", beds: 2, baths: 1, rent_pcm: 0, landlord: "", tenant: "", status: "Managed" });
      load();
    } catch { toast.error("Could not add property"); }
  };

  const filtered = !properties ? [] : (q.length >= 2
    ? properties.filter((p) =>
        [p.reference, p.address, p.city, p.postcode, p.landlord, p.tenant]
          .some((f) => (f || "").toLowerCase().includes(q)))
    : properties);

  return (
    <Layout>
      <div className="flex items-end justify-between mb-8 fade-up">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-blue-700 mb-2">Portfolio</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Properties</h1>
          <p className="text-slate-500 mt-1 text-sm" data-testid="properties-count">
            {q.length >= 2
              ? `${filtered.length} match${filtered.length === 1 ? "" : "es"} for "${searchParams.get("q")}"`
              : (view === "archived"
                  ? `${properties?.length ?? 0} archived ${(properties?.length ?? 0) === 1 ? "property" : "properties"}`
                  : `${properties?.length ?? 0} properties across your portfolio`)}
          </p>
        </div>
        <button data-testid="add-property-via-landlord-button" onClick={() => navigate("/landlords")}
          className="bg-blue-700 text-white hover:bg-blue-800 shadow-sm rounded-lg px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2">
          <Plus size={16} /> Add Property
        </button>
      </div>

      <div className="flex items-center gap-1 mb-6 fade-up">
        <button onClick={() => setView("active")} data-testid="properties-tab-active"
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${view === "active" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Active</button>
        <button onClick={() => setView("archived")} data-testid="properties-tab-archived"
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${view === "archived" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}><Archive size={14} /> Archived</button>
      </div>

      {!properties ? <Loading /> : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((p) => (
          <Card key={p.property_id} data-testid={`property-card-${p.reference}`}
            onClick={() => navigate(`/properties/${p.property_id}`)}
            className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow group fade-up">
            <div className="h-44 bg-slate-100 relative overflow-hidden">
              <img src={p.cover_file_id ? fileUrl(p.cover_file_id) : p.image} alt={p.address} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${view === "archived" ? "grayscale" : ""}`} />
              <div className="absolute top-3 left-3"><Badge>{view === "archived" ? "Archived" : p.status}</Badge></div>
              <div className="absolute top-3 right-3 flex items-center gap-2">
                {view === "archived" ? (
                  <button data-testid={`restore-property-${p.reference}`} onClick={(e) => restore(e, p)} title="Restore property"
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-white/95 hover:bg-emerald-600 hover:text-white text-emerald-700 text-xs font-semibold shadow-sm transition-colors">
                    <RotateCcw size={12} /> Restore
                  </button>
                ) : (
                  <>
                    <ReadyToLetBadge c={p.compliance} />
                    <button data-testid={`archive-property-${p.reference}`} onClick={(e) => archive(e, p)} title="Archive property (kept 7 years)"
                      className="w-7 h-7 rounded-full bg-white/90 hover:bg-red-600 hover:text-white text-red-600 flex items-center justify-center shadow-sm transition-colors opacity-0 group-hover:opacity-100">
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="p-5">
              <h3 className="font-semibold text-slate-900 truncate">{p.address}</h3>
              <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={13} /> {p.city}, {p.postcode}</p>
              <div className="flex items-center gap-4 mt-4 text-sm text-slate-600">
                <span className="flex items-center gap-1.5"><BedDouble size={15} /> {p.beds}</span>
                <span className="flex items-center gap-1.5"><Bath size={15} /> {p.baths}</span>
                <span className="ml-auto font-display font-bold text-slate-900 tabular-nums">{gbp(p.rent_pcm)}<span className="text-xs font-normal text-slate-400">/mo</span></span>
              </div>
              {view === "archived" && p.auto_delete_at && (
                <p className="mt-3 text-xs text-amber-700 bg-amber-50 rounded-md px-2 py-1.5">Archived · auto-deletes {new Date(p.auto_delete_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</p>
              )}
              <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500">
                <p className="truncate"><span className="text-slate-400">Landlord:</span>{" "}
                  {p.landlord ? (
                    <button data-testid={`card-landlord-${p.reference}`} onClick={(e) => { e.stopPropagation(); navigate(`/landlords?q=${encodeURIComponent(p.landlord)}`); }}
                      className="text-blue-700 hover:text-blue-900 hover:underline font-medium">{p.landlord}</button>
                  ) : "—"}
                </p>
                <p className="truncate mt-0.5"><span className="text-slate-400">Tenant:</span>{" "}
                  {p.tenant ? (
                    <button data-testid={`card-tenant-${p.reference}`} onClick={(e) => { e.stopPropagation(); navigate(`/properties/${p.property_id}?tab=rent`); }}
                      className="text-blue-700 hover:text-blue-900 hover:underline font-medium">{p.tenant}</button>
                  ) : "—"}
                </p>
                <AccessCodes property={p} />
              </div>
            </div>
          </Card>
        ))}
      </div>
      )}

      {properties && filtered.length === 0 && (
        <div data-testid="no-results" className="text-center py-20 text-slate-400">
          {view === "archived" ? (
            <>
              <Archive size={28} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No archived properties.</p>
              <p className="text-xs mt-1">Properties you archive will appear here and can be restored within 7 years.</p>
            </>
          ) : q.length >= 2 ? (
            <>
              <p className="text-sm">No properties match "{searchParams.get("q")}".</p>
              <p className="text-xs mt-1">Try a reference (e.g. PP-1042), address, city or postcode.</p>
            </>
          ) : (
            <p className="text-sm">No properties yet.</p>
          )}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">Add Property</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Reference" testid="prop-ref" value={form.reference} onChange={(v) => setForm({ ...form, reference: v })} required placeholder="PP-1050" />
                <Field label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} select options={["Managed", "Letting", "Sale", "Refurbishment"]} />
              </div>
              <AddressAutocomplete label="Address" testid="prop-address" value={form.address} required
                onChange={(v) => setForm({ ...form, address: v })}
                onSelect={({ address, city, postcode }) => setForm((f) => ({ ...f, address, city: city || f.city, postcode: postcode || f.postcode }))} />
              <div className="grid grid-cols-2 gap-4">
                <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} required placeholder="London" />
                <Field label="Postcode" value={form.postcode} onChange={(v) => setForm({ ...form, postcode: v })} required placeholder="N1 7GH" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Beds" type="number" value={form.beds} onChange={(v) => setForm({ ...form, beds: v })} />
                <Field label="Baths" type="number" value={form.baths} onChange={(v) => setForm({ ...form, baths: v })} />
                <Field label="Rent (pcm)" type="number" value={form.rent_pcm} onChange={(v) => setForm({ ...form, rent_pcm: v })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Landlord" value={form.landlord} onChange={(v) => setForm({ ...form, landlord: v })} />
                <Field label="Tenant" value={form.tenant} onChange={(v) => setForm({ ...form, tenant: v })} />
              </div>
              <button data-testid="submit-property-button" type="submit" className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium transition-colors">Add to Portfolio</button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, select, options, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      {select ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
          {options.map((o) => <option key={o}>{o}</option>)}
        </select>
      ) : (
        <input data-testid={testid} type={type} value={value} required={required} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600" />
      )}
    </div>
  );
}

export { Field };
